import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { useGeneral } from '../../contexts';
import Button from '../../components/ui/Button/Button';
import { Tabs, Tab } from '../../components/ui/Tabs';
import SEO from '../../components/SEO';
import { isValidEmail } from '../../utils/validators';
import formStyles from '../../components/ui/Form/Form.module.css';
import styles from './AuthPage.module.css';

interface PasswordValidation {
  reqLen: boolean;
  reqSpecial: boolean;
  reqNumber: boolean;
  reqUpper: boolean;
  reqLower: boolean;
  isValidPassword: boolean;
}

type TabType = 'login' | 'register';

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { auth, language } = useGeneral();
  const { login, register, isAuthenticated } = auth;
  const { getTranslation } = language;
  
  // Get redirect URL from query parameters (preserve across tab switches)
  const redirectUrl = searchParams.get('redirect') || undefined;
  
  // Determine initial tab based on route
  const [activeTab, setActiveTab] = useState<TabType>(
    location.pathname === '/register' ? 'register' : 'login'
  );
  const [imageLoading, setImageLoading] = useState(true);

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Register form state
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    reqLen: false,
    reqSpecial: false,
    reqNumber: false,
    reqUpper: false,
    reqLower: false,
    isValidPassword: false
  });

  const { setLoading, isLoading } = useGeneral();
  const [error, setError] = useState<string | null>(null);
  
  // Use centralized loading state
  const isLoadingState = isLoading('auth.login') || isLoading('auth.register');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Navigate to redirect URL if provided, otherwise go to homepage
      const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/';
      navigate(targetUrl);
    }
  }, [isAuthenticated, navigate, redirectUrl]);

  // Update tab when route changes
  useEffect(() => {
    if (location.pathname === '/register') {
      setActiveTab('register');
    } else if (location.pathname === '/login') {
      setActiveTab('login');
    }
  }, [location.pathname]);

  // Load saved username if available
  useEffect(() => {
    const savedUsername = localStorage.getItem('username') || localStorage.getItem('temp_username');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (savedUsername) {
      setUsername(savedUsername);
    }
    if (savedRememberMe) {
      setRememberMe(true);
    }
  }, []);

  // Validate password in real-time
  useEffect(() => {
    const reqLen = registerPassword.length >= 8;
    const reqSpecial = /[^A-Za-z0-9ÇÖÜĞŞİçöüğşı]/.test(registerPassword);
    const reqNumber = /\d/.test(registerPassword);
    const reqUpper = /[A-ZÇĞİÖŞÜ]/.test(registerPassword);
    const reqLower = /[a-zçğıöşü]/.test(registerPassword);
    const isValidPassword = reqLen && reqSpecial && reqNumber && reqUpper && reqLower;
    
    const validation: PasswordValidation = {
      reqLen,
      reqSpecial,
      reqNumber,
      reqUpper,
      reqLower,
      isValidPassword
    };
    setPasswordValidation(validation);
  }, [registerPassword]);

  // Remove spaces from password
  const handleRegisterPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setRegisterPassword(value);
  };

  const isPasswordValid = () => {
    return passwordValidation.isValidPassword;
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setError(null);
    // Preserve redirect parameter when switching tabs
    const redirectParam = redirectUrl ? `?redirect=${encodeURIComponent(redirectUrl)}` : '';
    if (tab === 'login') {
      navigate(`/login${redirectParam}`);
    } else {
      navigate(`/register${redirectParam}`);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading('auth.login', true);

    try {
      await login(username, password, rememberMe, redirectUrl);
    } catch (err: any) {
      setError(err.message || getTranslation('auth.loginFailed'));
    } finally {
      setLoading('auth.login', false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isPasswordValid()) {
      setError(getTranslation('auth.passwordRequirements'));
      return;
    }

    if (!isValidEmail(email)) {
      setError(getTranslation('profile.invalidEmailFormat'));
      return;
    }

    setLoading('auth.register', true);

    try {
      await register(name, lastname, email, registerPassword, redirectUrl);
    } catch (err: any) {
      const errorMessage = err.message || getTranslation('auth.registrationFailed');
      const lowerMsg = errorMessage.toLowerCase();
      if (lowerMsg.includes('already') || lowerMsg.includes('zaten') || lowerMsg.includes('duplicate') || lowerMsg.includes('exists')) {
        setError(getTranslation('auth.emailAlreadyInUse'));
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading('auth.register', false);
    }
  };

  const isRegister = activeTab === 'register';
  const pageTitle = isRegister ? getTranslation('auth.register') : getTranslation('auth.login');

  return (
    <>
      <SEO
        title={pageTitle}
        description={isRegister ? 'Create your ToGather account.' : 'Log in to ToGather.'}
        url={typeof window !== 'undefined' ? `${window.location.origin}${location.pathname}` : undefined}
        noindex
        lang={language.language}
      />
      <div 
        className={styles.authPage}
        style={{
          backgroundImage: 'url(/form-background.jpg)'
        }}
      >
        <div className={styles.authContainer}>
          <div className={styles.brandSection}>
            {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', width: '48px', height: '48px', borderRadius: '8px' }} />}
            <img 
              src="/lodos-favicon.png" 
              alt="Lodos Logo" 
              className={styles.logo}
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
            />
            <h1 className={styles.brandTitle}>ToGather</h1>
          </div>
          
          <div className={styles.welcomeSection}>
            <h2 className={styles.welcomeTitle}>{getTranslation('auth.welcome')}</h2>
            {isRegister && (
              <p className={styles.welcomeSubtitle}>{getTranslation('auth.completeRegistration')}</p>
            )}
          </div>

          <Tabs variant="underline">
            <Tab
              variant="underline"
              isActive={activeTab === 'login'}
              onClick={() => handleTabChange('login')}
            >
              {getTranslation('auth.login')}
            </Tab>
            <Tab
              variant="underline"
              isActive={activeTab === 'register'}
              onClick={() => handleTabChange('register')}
            >
              {getTranslation('auth.register')}
            </Tab>
          </Tabs>

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {activeTab === 'login' ? (
            <form onSubmit={handleLogin} className={formStyles.form}>
              <div className={formStyles.formField}>
                <label htmlFor="username" className={styles.label}>
                  {getTranslation('auth.email')}
                </label>
                <input
                  id="username"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={styles.input}
                  required
                  disabled={isLoadingState}
                />
              </div>

              <div className={formStyles.formField}>
                <label htmlFor="password" className={styles.label}>
                  {getTranslation('auth.password')}
                </label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={styles.input}
                    required
                    disabled={isLoadingState}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoadingState}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    {showPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                  </button>
                </div>
              </div>

              <div className={styles.rememberMeGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoadingState}
                    className={styles.checkbox}
                  />
                  <span>{getTranslation('auth.rememberMe')}</span>
                </label>
                <Link to="/forgot-password" className={styles.forgotPasswordLink}>
                  {getTranslation('auth.forgotPasswordLink')}
                </Link>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={isLoadingState}
                className={styles.submitButton}
              >
                {isLoadingState ? getTranslation('auth.loggingIn') : getTranslation('auth.loginButton')}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className={formStyles.form}>
              <div className={formStyles.formRow}>
                <div className={formStyles.formField}>
                  <label htmlFor="name" className={styles.label}>
                    {getTranslation('auth.firstName')}
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={styles.input}
                    required
                    disabled={isLoadingState}
                  />
                </div>

                <div className={formStyles.formField}>
                  <label htmlFor="lastname" className={styles.label}>
                    {getTranslation('auth.lastName')}
                  </label>
                  <input
                    id="lastname"
                    type="text"
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                    className={styles.input}
                    required
                    disabled={isLoadingState}
                  />
                </div>
              </div>

              <div className={formStyles.formField}>
                <label htmlFor="email" className={styles.label}>
                  {getTranslation('auth.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  required
                  disabled={isLoadingState}
                />
              </div>

              <div className={formStyles.formField}>
                <label htmlFor="registerPassword" className={styles.label}>
                  {getTranslation('auth.password')}
                </label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    id="registerPassword"
                    type={showRegisterPassword ? 'text' : 'password'}
                    value={registerPassword}
                    onChange={handleRegisterPasswordChange}
                    className={styles.input}
                    required
                    disabled={isLoadingState}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                    disabled={isLoadingState}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    {showRegisterPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                  </button>
                </div>

                {registerPassword && (
                  <div className={styles.passwordRequirements}>
                    <div className={passwordValidation.reqLen ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordMinLength')}
                    </div>
                    <div className={passwordValidation.reqSpecial ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordSpecialChar')}
                    </div>
                    <div className={passwordValidation.reqNumber ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordNumber')}
                    </div>
                    <div className={passwordValidation.reqUpper ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordUppercase')}
                    </div>
                    <div className={passwordValidation.reqLower ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordLowercase')}
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={isLoadingState || !isPasswordValid()}
                className={styles.submitButton}
              >
                {isLoadingState ? getTranslation('auth.registering') : getTranslation('auth.registerButton')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default AuthPage;

